// Sales Report — a dedicated, sales-only report over the agency's policies.
// 100% client-side: it reads from the same `policies` array the rest of the
// CRM already hydrated (every page), so there's no backend roundtrip and no
// schema change. Each policy = one sale row. Filter by policy type, carrier,
// analyst, who processed it, agent, and a sale-date range; the table shows the
// columns Lissette asked for (client, type, carrier, commission, plan, sale
// date, analyst, agent, effective date, expiration date) and exports to
// Excel/CSV.

import { useEffect, useMemo, useState } from 'react';
import { BarChart2, Download, Filter, X, DollarSign, Calendar, Settings2 } from 'lucide-react';
import { fmt } from '../utils.js';
import { getPolicyType } from '../policyTypes.js';
import { toast } from './Toast.jsx';
import { updatePolicy as apiUpdatePolicy } from '../api.js';
import { GroupedChart } from './teamReportChart.jsx';
import DateField from './DateField.jsx';
import { contarEtiquetas, pasaElFiltroDeEtiqueta, textoSinEtiqueta, SIN_ETIQUETA } from '../filtroDeEtiquetas.js';
// LA MISMA comision que Comisiones por Poliza (Lissette 2026-10-11: "estos
// datos tienen que ser los mismos en los dos lados"): misma cuenta, mismos
// campos y mismo guardado.
import { cuentaDeComision, valoresDeComision, polizaConComision } from '../comisionDePoliza.js';

// What a sale can be grouped by, and what can be added up. Both read straight
// off the policy row the table already renders.
const SALES_DIMENSIONS = (es) => [
  { key: 'analyst', label: es ? 'Analista (quién vendió)' : 'Analyst (who sold)', get: (p) => p.analystName },
  { key: 'type', label: es ? 'Tipo de póliza' : 'Policy type', get: (p) => p.type },
  { key: 'carrier', label: es ? 'Compañía' : 'Carrier', get: (p) => p.carrier },
  { key: 'processedBy', label: es ? 'Quién procesó' : 'Processed by', get: (p) => p.processedBy },
  { key: 'agent', label: es ? 'Agente' : 'Agent', get: (p) => agentOf(p) },
  { key: 'plan', label: es ? 'Plan' : 'Plan', get: (p) => p.plan },
  // timeline: se ordena por fecha, no por tamaño — ver ReportChart.preserveOrder.
  { key: 'saleMonth', label: es ? 'Mes de venta' : 'Sale month', get: (p) => monthKeyOf(p.saleDate), timeline: true },
];
const SALES_MEASURES = (es) => [
  { key: 'commission', label: es ? 'Comisión $' : 'Commission $', get: (p) => cuentaDeComision(p).total, unit: 'money' },
  { key: 'premium', label: es ? 'Prima mensual $' : 'Monthly premium $', get: (p) => parseNum(p.monthlyPremium), unit: 'money' },
];

// Pull a clean number out of a string that may carry "$", "%", commas, etc.
function parseNum(v) {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function money(v) {
  const n = parseNum(v);
  if (!v && v !== 0) return '';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(v) {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  return s.endsWith('%') ? s : `${s}%`;
}

// "YYYY-MM" from a sale date, using LOCAL parts. toISOString() would shift the
// day in Florida and file a sale made on the 1st into the previous month.
function monthKeyOf(v) {
  if (!v) return '';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? new Date(`${v}T00:00:00`) : new Date(v);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// The agent shown on a policy card: the explicit label wins, then the FK agent.
const agentOf = (p) => (p.agentLabel || p.agentName || '').trim();

// Inline export helpers — kept local so this view is self-contained (mirrors
// ReportsClientsView). Excel is lazy-loaded only when the user clicks export.
export async function exportToExcel(data, filename) {
  if (!data.length) return;
  const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
  XLSX.writeFile(wb, filename);
}
export function exportToCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(','),
    ...data.map((row) => headers.map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function SalesReportView({ policies = [], clients = [], users = [], onPoliciesChange, lang = 'en', userPrefs = {}, onPrefsChange }) {
  const es = lang === 'es';

  // Resolve a display name for the client on each policy (card already carries
  // clientName; fall back to a lookup for older shapes).
  const clientNameOf = useMemo(() => {
    const byId = new Map((clients || []).map((c) => [c.id, `${c.firstName || ''} ${c.lastName || ''}`.trim()]));
    return (p) => p.clientName || byId.get(p.clientId) || '';
  }, [clients]);

  // Filter state — initialized from the user's saved filters (uiPrefs → server,
  // so the chosen view follows them across devices instead of being lost).
  const savedF = (userPrefs && userPrefs.salesReportFilters) || {};
  const [fType, setFType] = useState(savedF.fType || '');
  const [fCarrier, setFCarrier] = useState(savedF.fCarrier || '');
  const [fAnalyst, setFAnalyst] = useState(savedF.fAnalyst || '');
  const [fProcessed, setFProcessed] = useState(savedF.fProcessed || '');
  const [fAgent, setFAgent] = useState(savedF.fAgent || '');
  const [saleFrom, setSaleFrom] = useState(savedF.saleFrom || '');
  const [saleTo, setSaleTo] = useState(savedF.saleTo || '');
  // Tags propios de la PÓLIZA y del CLIENTE (Lissette 2026-10-20). Mismo
  // filtro que las listas de Pólizas y Clientes (filtroDeEtiquetas.js):
  // '' = todas, 'none' = sin tag, otro = ese tag sin distinguir mayúsculas.
  const [fPolTag, setFPolTag] = useState(savedF.fPolTag || '');
  const [fCliTag, setFCliTag] = useState(savedF.fCliTag || '');
  const clienteDe = useMemo(() => new Map((clients || []).map((c) => [c.id, c])), [clients]);
  const tagsDePolizas = useMemo(() => contarEtiquetas(policies), [policies]);
  const tagsDeClientes = useMemo(() => contarEtiquetas(clients || []), [clients]);
  // Persist the chosen filters per-user (server-synced).
  useEffect(() => {
    if (!onPrefsChange) return;
    onPrefsChange((p) => ({ ...p, salesReportFilters: { fType, fCarrier, fAnalyst, fProcessed, fAgent, saleFrom, saleTo, fPolTag, fCliTag } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fType, fCarrier, fAnalyst, fProcessed, fAgent, saleFrom, saleTo, fPolTag, fCliTag]);

  // Dropdown options derived from the data itself, so only values that actually
  // exist appear (no empty/irrelevant choices).
  const opts = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean).map((s) => String(s).trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const types = [...new Set(policies.map((p) => p.type).filter(Boolean))]
      .map((id) => ({ id, label: (() => { const pt = getPolicyType(id); return `${pt.icon || ''} ${es ? (pt.labelEs || pt.label) : pt.label}`.trim(); })() }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return {
      types,
      carriers: uniq(policies.map((p) => p.carrier)),
      analysts: uniq(policies.map((p) => p.analystName)),
      processed: uniq(policies.map((p) => p.processedBy)),
      agents: uniq(policies.map(agentOf)),
    };
  }, [policies, es]);

  // Click-to-sort by any column (Lissette 2026-06-18). Default = sale date,
  // newest first. Clicking a column toggles asc/desc; dates default to desc.
  const [sortKey, setSortKey] = useState('saleDate');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir((key === 'saleDate' || key === 'effective' || key === 'expiration') ? 'desc' : 'asc'); }
  };
  const NUMERIC_SORT = new Set(['commission', 'commPct', 'pctPrima', 'premium']);
  const DATE_SORT = { saleDate: 'saleDate', effective: 'effectiveDate', expiration: 'renewalDate' };
  const sortVal = (p, key) => {
    if (NUMERIC_SORT.has(key)) {
      if (key === 'commission') return cuentaDeComision(p, valoresDeComision(p, cDrafts[p.id] || {})).total;
      if (key === 'premium') return parseNum(p.monthlyPremium);
      return parseNum(key === 'pctPrima' ? p.porcentajeDePrima : p.commissionPercent);
    }
    if (DATE_SORT[key]) return p[DATE_SORT[key]] || '';
    switch (key) {
      case 'client': return clientNameOf(p).toLowerCase();
      case 'type': { const pt = getPolicyType(p.type); return (es ? (pt.labelEs || pt.label) : pt.label).toLowerCase(); }
      case 'carrier': return String(p.carrier || '').toLowerCase();
      case 'plan': return String(p.plan || '').toLowerCase();
      case 'analyst': return String(p.analystName || '').toLowerCase();
      case 'agent': return agentOf(p).toLowerCase();
      default:
        if (key.startsWith('cf:')) { const l = key.slice(3); const f = Array.isArray(p.customFields) ? p.customFields.find((x) => String(x?.label || '').trim() === l) : null; return String(f?.value || '').toLowerCase(); }
        return '';
    }
  };

  // CASILLAS DE COMISION EDITABLES: escriben los MISMOS campos de la poliza
  // que Comisiones por Poliza, y se guardan con la MISMA funcion
  // (comisionDePoliza.js). Lo que se llena aqui sale alla y al reves.
  //   pctPrima → porcentajeDePrima (% que paga la aseguradora sobre la prima)
  //   pct      → commissionPercent (% split, lo que nos queda)
  //   amt      → commissionAmount  (solo se escribe a mano si no hay cuenta)
  // El total se calcula: no se puede escribir encima de una cuenta, porque la
  // otra tabla lo volveria a pisar al siguiente guardado.
  const [cDrafts, setCDrafts] = useState({}); // policyId -> { pct, pctPrima, amt }
  const cval = (p, k) => String(valoresDeComision(p, cDrafts[p.id] || {})[k] ?? '');
  const setCDraft = (p, k, v) => setCDrafts((d) => ({ ...d, [p.id]: { ...(d[p.id] || {}), [k]: v } }));
  const saveCommission = async (p) => {
    const borrador = cDrafts[p.id];
    if (!borrador) return;
    setCDrafts((d) => { const c = { ...d }; delete c[p.id]; return c; });
    const { siguiente: next, cambio } = polizaConComision(p, borrador);
    if (!cambio) return;
    onPoliciesChange && onPoliciesChange((prev) => (prev || []).map((x) => (x.id === p.id ? next : x)));
    if (typeof window !== 'undefined' && !!window.__deskteAuth) {
      try { await apiUpdatePolicy(p.id, next); }
      catch (e) { toast.error((es ? 'No se pudo guardar la comisión: ' : 'Could not save commission: ') + (e.message || e)); }
    }
  };

  // Apply filters, then sort by the chosen column/direction.
  const rows = useMemo(() => {
    const out = policies.filter((p) => {
      if (fType && p.type !== fType) return false;
      if (fCarrier && String(p.carrier || '').trim() !== fCarrier) return false;
      if (fAnalyst && String(p.analystName || '').trim() !== fAnalyst) return false;
      if (fProcessed && String(p.processedBy || '').trim() !== fProcessed) return false;
      if (fAgent && agentOf(p) !== fAgent) return false;
      const sd = p.saleDate || '';
      if (saleFrom && (!sd || sd < saleFrom)) return false;
      if (saleTo && (!sd || sd > saleTo)) return false;
      if (fPolTag && !pasaElFiltroDeEtiqueta(p, fPolTag)) return false;
      if (fCliTag && !pasaElFiltroDeEtiqueta(clienteDe.get(p.clientId) || { tags: [] }, fCliTag)) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return out.sort((a, b) => {
      const va = sortVal(a, sortKey); const vb = sortVal(b, sortKey);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [policies, fType, fCarrier, fAnalyst, fProcessed, fAgent, saleFrom, saleTo, fPolTag, fCliTag, clienteDe, sortKey, sortDir, es, clientNameOf]);

  // Single source of truth for columns: both the table and the export read it.
  const columns = useMemo(() => [
    { key: 'client',     label: es ? 'Cliente' : 'Client',           get: (p) => clientNameOf(p) },
    { key: 'type',       label: es ? 'Tipo de Póliza' : 'Policy Type', get: (p) => { const pt = getPolicyType(p.type); return es ? (pt.labelEs || pt.label) : pt.label; } },
    { key: 'carrier',    label: es ? 'Compañía' : 'Carrier',          get: (p) => p.carrier || '' },
    // PRIMA MENSUAL: la de la poliza, tal cual (Lissette 2026-10-11).
    { key: 'premium',    label: es ? 'Prima mensual' : 'Monthly premium', get: (p) => (String(p.monthlyPremium ?? '').trim() === '' ? '' : money(p.monthlyPremium || '0')) },
    { key: 'commission', label: es ? 'Comisión' : 'Commission',       get: (p) => money(cuentaDeComision(p).total) },
    { key: 'pctPrima',   label: es ? '% de la prima' : '% of premium', get: (p) => pct(p.porcentajeDePrima) },
    { key: 'commPct',    label: es ? '% Split' : 'Split %',           get: (p) => pct(p.commissionPercent) },
    { key: 'plan',       label: es ? 'Plan' : 'Plan',                 get: (p) => p.plan || '' },
    { key: 'saleDate',   label: es ? 'Fecha de Venta' : 'Sale Date',  get: (p) => (p.saleDate ? fmt(p.saleDate) : '') },
    { key: 'analyst',    label: es ? 'Analista' : 'Analyst',          get: (p) => p.analystName || '' },
    { key: 'agent',      label: es ? 'Agente' : 'Agent',              get: (p) => agentOf(p) },
    { key: 'effective',  label: es ? 'Fecha Activa' : 'Effective Date', get: (p) => (p.effectiveDate ? fmt(p.effectiveDate) : '') },
    { key: 'expiration', label: es ? 'Fecha Expiración' : 'Expiration Date', get: (p) => (p.renewalDate ? fmt(p.renewalDate) : '') },
  ], [es, clientNameOf]);

  // ── Custom columns (Lissette 2026-06-13) ─────────────────────────────────
  // Any standard column can be hidden, and any per-policy custom field
  // ([{label,value}]) can be surfaced as its own column. The choice is saved
  // per-user in uiPrefs (server-synced) so it follows the agent across devices.
  const customFieldLabels = useMemo(() => {
    const set = new Set();
    for (const p of policies) {
      if (Array.isArray(p.customFields)) {
        for (const f of p.customFields) { const l = String(f?.label || '').trim(); if (l) set.add(l); }
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [policies]);
  const colCfg = (userPrefs && userPrefs.salesReportCols) || {};
  const hidden = Array.isArray(colCfg.hidden) ? colCfg.hidden : [];
  // Keep only chosen custom-field columns whose label still exists in the data.
  const extraCf = (Array.isArray(colCfg.cf) ? colCfg.cf : []).filter((l) => customFieldLabels.includes(l));
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const saveColCfg = (patch) => { if (onPrefsChange) onPrefsChange((p) => ({ ...p, salesReportCols: { hidden, cf: extraCf, ...patch } })); };
  const toggleStandard = (key) => saveColCfg({ hidden: hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key] });
  const toggleCf = (label) => saveColCfg({ cf: extraCf.includes(label) ? extraCf.filter((l) => l !== label) : [...extraCf, label] });
  const cfValue = (p, label) => {
    const f = Array.isArray(p.customFields) ? p.customFields.find((x) => String(x?.label || '').trim() === label) : null;
    return f ? (f.value || '') : '';
  };
  // The columns actually rendered/exported: visible standard ones + chosen
  // custom-field columns appended at the end.
  const visibleColumns = [
    ...columns.filter((c) => !hidden.includes(c.key)),
    ...extraCf.map((label) => ({ key: 'cf:' + label, label, get: (p) => cfValue(p, label), custom: true })),
  ];

  const totalCommission = useMemo(() => rows.reduce((s, p) => s + cuentaDeComision(p, valoresDeComision(p, cDrafts[p.id] || {})).total, 0), [rows, cDrafts]);

  // No pagination (Lissette 2026-06-14): the report shows every matching row
  // in one continuous list, with a TOTAL row at the bottom that sums the
  // commission column for the current filter.

  const hasFilters = fType || fCarrier || fAnalyst || fProcessed || fAgent || saleFrom || saleTo || fPolTag || fCliTag;
  const clearFilters = () => { setFType(''); setFCarrier(''); setFAnalyst(''); setFProcessed(''); setFAgent(''); setSaleFrom(''); setSaleTo(''); setFPolTag(''); setFCliTag(''); };

  const exportRows = () => rows.map((p) => {
    const o = {};
    for (const c of visibleColumns) o[c.label] = c.get(p);
    return o;
  });
  const _d = new Date();
  const today = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
  const filenameBase = `deskte-reporte-ventas-${today}`;

  const selStyle = { fontSize: '.82rem', padding: '.45rem .6rem', cursor: 'pointer', minWidth: 0, width: '100%' };
  const Field = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <label style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</label>
      {children}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <div className="dkt-sec-title">
          <BarChart2 size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
          {es ? 'Reporte de Ventas' : 'Sales Report'}
        </div>
        <div className="dkt-sec-sub">
          {es
            ? 'Filtra tus pólizas vendidas por tipo, compañía, analista, quién procesó, agente, fecha de venta y tags de póliza o de cliente.'
            : 'Filter your sold policies by type, carrier, analyst, who processed, agent, sale date and policy or client tags.'}
        </div>
      </div>

      {/* Filters */}
      <div className="dkt-card" style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Filter size={14} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>
            {es ? 'Filtros' : 'Filters'}
          </span>
          {hasFilters && (
            <button onClick={clearFilters} style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'transparent', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)',
              fontSize: '.72rem', fontWeight: 600, padding: '.3rem .6rem', borderRadius: 7, cursor: 'pointer',
            }}>
              <X size={11} /> {es ? 'Limpiar filtros' : 'Clear filters'}
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '.7rem' }}>
          <Field label={es ? 'Tipo de Póliza' : 'Policy Type'}>
            <select className="dkt-inp" style={selStyle} value={fType} onChange={(e) => setFType(e.target.value)}>
              <option value="">{es ? 'Todos' : 'All'}</option>
              {opts.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label={es ? 'Compañía' : 'Carrier'}>
            <select className="dkt-inp" style={selStyle} value={fCarrier} onChange={(e) => setFCarrier(e.target.value)}>
              <option value="">{es ? 'Todas' : 'All'}</option>
              {opts.carriers.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={es ? 'Analista' : 'Analyst'}>
            <select className="dkt-inp" style={selStyle} value={fAnalyst} onChange={(e) => setFAnalyst(e.target.value)}>
              <option value="">{es ? 'Todos' : 'All'}</option>
              {opts.analysts.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label={es ? 'Procesado por' : 'Processed by'}>
            <select className="dkt-inp" style={selStyle} value={fProcessed} onChange={(e) => setFProcessed(e.target.value)}>
              <option value="">{es ? 'Todos' : 'All'}</option>
              {opts.processed.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label={es ? 'Agente' : 'Agent'}>
            <select className="dkt-inp" style={selStyle} value={fAgent} onChange={(e) => setFAgent(e.target.value)}>
              <option value="">{es ? 'Todos' : 'All'}</option>
              {opts.agents.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label={es ? 'Tag de póliza' : 'Policy tag'}>
            <select className="dkt-inp" style={selStyle} value={fPolTag} onChange={(e) => setFPolTag(e.target.value)}>
              <option value="">{es ? 'Todos' : 'All'}</option>
              {tagsDePolizas.sinEtiqueta > 0 && <option value={SIN_ETIQUETA}>{textoSinEtiqueta(tagsDePolizas.sinEtiqueta, lang)}</option>}
              {tagsDePolizas.opciones.map((t) => <option key={t.label} value={t.label}>{t.label} ({t.n})</option>)}
              {fPolTag && fPolTag !== SIN_ETIQUETA && !tagsDePolizas.opciones.some((t) => t.label.toLowerCase() === fPolTag.toLowerCase()) && <option value={fPolTag}>{fPolTag}</option>}
            </select>
          </Field>
          <Field label={es ? 'Tag de cliente' : 'Client tag'}>
            <select className="dkt-inp" style={selStyle} value={fCliTag} onChange={(e) => setFCliTag(e.target.value)}>
              <option value="">{es ? 'Todos' : 'All'}</option>
              {tagsDeClientes.sinEtiqueta > 0 && <option value={SIN_ETIQUETA}>{textoSinEtiqueta(tagsDeClientes.sinEtiqueta, lang)}</option>}
              {tagsDeClientes.opciones.map((t) => <option key={t.label} value={t.label}>{t.label} ({t.n})</option>)}
              {fCliTag && fCliTag !== SIN_ETIQUETA && !tagsDeClientes.opciones.some((t) => t.label.toLowerCase() === fCliTag.toLowerCase()) && <option value={fCliTag}>{fCliTag}</option>}
            </select>
          </Field>
          <Field label={es ? 'Venta desde' : 'Sale from'}>
            {/* El selector de Deskte, no el del navegador (2026-10-20: "no deja
                cambiar el año cuando se escoge el rango"). El nativo dispara
                onChange con cada tecla del año (0002, 0020, 0202…): el filtro
                y el guardado de preferencias corrían con fechas a medias. Este
                tiene desplegable de año y solo avisa con una fecha completa. */}
            <DateField style={selStyle} value={saleFrom} onChange={(e) => setSaleFrom(e.target.value)} lang={es ? 'es' : 'en'} />
          </Field>
          <Field label={es ? 'Venta hasta' : 'Sale to'}>
            <DateField style={selStyle} value={saleTo} onChange={(e) => setSaleTo(e.target.value)} lang={es ? 'es' : 'en'} />
          </Field>
        </div>
      </div>

      {/* Summary KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '.75rem' }}>
        <div className="dkt-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 4, borderLeft: '3px solid #00d4ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{es ? 'Ventas' : 'Sales'}</span>
            <Calendar size={14} style={{ color: '#00d4ff' }} />
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>{rows.length}</div>
          <div style={{ fontSize: 11, color: 'var(--c-text3,#45688a)' }}>{es ? 'pólizas en el filtro' : 'policies in filter'}</div>
        </div>
        <div className="dkt-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 4, borderLeft: '3px solid #00ff88' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{es ? 'Comisión total' : 'Total commission'}</span>
            <DollarSign size={14} style={{ color: '#00ff88' }} />
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>
            {`$${totalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--c-text3,#45688a)' }}>{es ? 'suma del filtro actual' : 'sum of current filter'}</div>
        </div>
      </div>

      {/* Table + export */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.75rem', flexWrap: 'wrap', gap: '.5rem' }}>
          <div style={{ fontSize: '.85rem', color: 'var(--c-text2,#7aa3c0)' }}>
            <strong style={{ color: 'var(--c-cyan,#00d4ff)' }}>{rows.length}</strong> {es ? 'resultados' : 'results'}
          </div>
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setColMenuOpen((o) => !o)} className="dkt-btn-g">
                <Settings2 size={13} /> {es ? 'Columnas' : 'Columns'}
              </button>
              {colMenuOpen && (
                <>
                  <div onClick={() => setColMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, width: 250, maxHeight: 380, overflowY: 'auto', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border2,#1f3f60)', borderRadius: 12, boxShadow: '0 16px 50px rgba(0,0,0,.5)', padding: '.6rem' }}>
                    <div style={{ fontSize: '.64rem', fontWeight: 800, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 .3rem .35rem' }}>
                      {es ? 'Mostrar columnas' : 'Show columns'}
                    </div>
                    {columns.map((c) => (
                      <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.32rem .3rem', cursor: 'pointer', fontSize: '.8rem', color: 'var(--c-text,#c8dff0)' }}>
                        <input type="checkbox" checked={!hidden.includes(c.key)} onChange={() => toggleStandard(c.key)} style={{ cursor: 'pointer' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label}</span>
                      </label>
                    ))}
                    <div style={{ height: 1, background: 'var(--c-border,#1a3550)', margin: '.45rem .2rem' }} />
                    <div style={{ fontSize: '.64rem', fontWeight: 800, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 .3rem .35rem' }}>
                      {es ? 'Campos personalizados' : 'Custom fields'}
                    </div>
                    {customFieldLabels.length === 0 ? (
                      <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', padding: '.2rem .3rem .3rem', lineHeight: 1.4 }}>
                        {es ? 'No hay campos personalizados en tus pólizas todavía. Agrégalos en una póliza (sección “Campos personalizados”) y aparecerán aquí.' : 'No custom fields on your policies yet. Add them on a policy and they will show up here.'}
                      </div>
                    ) : customFieldLabels.map((l) => (
                      <label key={l} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.32rem .3rem', cursor: 'pointer', fontSize: '.8rem', color: 'var(--c-text,#c8dff0)' }}>
                        <input type="checkbox" checked={extraCf.includes(l)} onChange={() => toggleCf(l)} style={{ cursor: 'pointer' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => exportToExcel(exportRows(), `${filenameBase}.xlsx`)} disabled={rows.length === 0} className="dkt-btn-g">
              <Download size={13} /> Excel
            </button>
            <button onClick={() => exportToCSV(exportRows(), `${filenameBase}.csv`)} disabled={rows.length === 0} className="dkt-btn-g">
              <Download size={13} /> CSV
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--c-text3,#45688a)', background: 'var(--c-surface,#0d1c2e)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 12 }}>
            {es ? 'Sin ventas para los criterios elegidos.' : 'No sales for the chosen criteria.'}
          </div>
        ) : (
          <>
          {/* Chart over the SAME filtered rows the table shows, so the two can
              never disagree — no second query, no second filter. */}
          <GroupedChart
            rows={rows}
            lang={lang}
            dimensions={SALES_DIMENSIONS(es)}
            measures={SALES_MEASURES(es)}
            color="#00d4ff"
          />
          <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
                <thead>
                  <tr style={{ background: 'var(--c-surface2,#112538)' }}>
                    {visibleColumns.map((c) => (
                      <th key={c.key} onClick={() => toggleSort(c.key)} title={es ? 'Ordenar por esta columna' : 'Sort by this column'} style={{ textAlign: 'left', padding: '.6rem .85rem', color: sortKey === c.key ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--c-border,#1a3550)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                        {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid rgba(26,53,80,.4)' }}>
                      {visibleColumns.map((c) => {
                        const value = c.get(p);
                        const cellStyle = c.key === 'client'
                          ? { color: 'var(--c-white,#eef6ff)', fontWeight: 600 }
                          : c.key === 'commission'
                            ? { color: 'var(--c-green,#00ff88)', fontFamily: 'var(--font-mono)', fontWeight: 600, whiteSpace: 'nowrap' }
                            : (c.key === 'saleDate' || c.key === 'effective' || c.key === 'expiration' || c.key === 'commPct' || c.key === 'pctPrima' || c.key === 'premium')
                              ? { color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }
                              : { color: 'var(--c-text2,#7aa3c0)' };
                        // Que se puede escribir en esta fila. El total solo cuando no
                        // hay cuenta (comisionDePoliza.js); el % de la prima no
                        // aplica a salud, que se cobra por persona.
                        const cu = cuentaDeComision(p, valoresDeComision(p, cDrafts[p.id] || {}));
                        const editKey = c.key === 'commission' ? (cu.como === 'manual' ? 'amt' : null)
                          : c.key === 'commPct' ? 'pct'
                          : c.key === 'pctPrima' ? (cu.formato === 'salud' ? null : 'pctPrima')
                          : null;
                        const shown = c.key === 'commission' ? money(cu.total) : value;
                        return (
                          <td key={c.key} style={{ padding: '.55rem .85rem', ...cellStyle }}
                            title={c.key === 'commission' && cu.como !== 'manual' ? (es ? 'Calculada igual que en Comisiones por Póliza' : 'Computed the same as Commissions by Policy') : undefined}>
                            {editKey ? (
                              <input value={cval(p, editKey)} onChange={(e) => setCDraft(p, editKey, e.target.value)} onBlur={() => saveCommission(p)}
                                inputMode="decimal" placeholder={editKey === 'amt' ? '$' : '%'}
                                style={{ width: editKey === 'amt' ? 92 : 64, boxSizing: 'border-box', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 6, color: editKey === 'amt' ? 'var(--c-green,#00ff88)' : 'var(--c-text,#c8dff0)', padding: '.2rem .4rem', fontSize: '.78rem', fontFamily: 'var(--font-mono)', outline: 'none' }} />
                            ) : (shown || (c.key === 'client' ? '' : '—'))}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--c-surface2,#112538)', borderTop: '2px solid var(--c-border,#1a3550)' }}>
                    {visibleColumns.map((c, i) => (
                      <td key={c.key} style={{
                        padding: '.65rem .85rem',
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                        color: c.key === 'commission' ? 'var(--c-green,#00ff88)' : 'var(--c-white,#eef6ff)',
                        fontFamily: c.key === 'commission' ? 'var(--font-mono)' : 'var(--font-display)',
                      }}>
                        {i === 0 ? 'TOTAL' : c.key === 'commission' ? money(totalCommission) : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
